
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Article } from "@/types/article";
import EditArticleForm from "@/components/admin/EditArticleForm";
import { mapSupabaseArticleToArticle } from "@/utils/mapSupabaseArticleToArticle";
import EditArticleLoading from "@/components/admin/EditArticleLoading";
import EditArticleError from "@/components/admin/EditArticleError";
import EditArticleNotFound from "@/components/admin/EditArticleNotFound";
import { mockArticles } from "@/utils/mockArticles";

const EditArticle = () => {
  const { id } = useParams();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase
          .from("articles")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (error) {
          console.log("Supabase fetch error:", error);
          const mockArticle = mockArticles.find(a => a.id.toString() === id?.toString());
          if (mockArticle) {
            setArticle(mockArticle);
          } else {
            setError(`Article with ID ${id} not found`);
          }
        } else if (data) {
          setArticle(mapSupabaseArticleToArticle(data));
        } else {
          setError(`Article with ID ${id} not found`);
        }
      } catch (err) {
        console.error("Error fetching article:", err);
        setError("Failed to load article");
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  if (loading) return <EditArticleLoading />;

  if (error) return <EditArticleError error={error} />;

  if (!article) return <EditArticleNotFound />;

  return (
    <div className="flex flex-col items-center pt-10 min-h-screen bg-gray-50">
      {article && (
        <EditArticleForm initialArticle={article} articleId={id ?? ""} />
      )}
    </div>
  );
};

export default EditArticle;
